import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../../../mocks/blogPosts';

// Rotating dealer myths - one per week
const dealerMyths = [
  {
    myth: "The deal expires tonight.",
    reality: "It expired last Tuesday. And the Tuesday before that."
  },
  {
    myth: "This is our cost.",
    reality: "Their cost is whatever number gets you to sign."
  },
  {
    myth: "We're losing money on this deal.",
    reality: "They've never lost money on a deal. Ever."
  },
  {
    myth: "The manager has to approve this price.",
    reality: "The manager approved it before you walked in."
  },
  {
    myth: "That's just the market right now.",
    reality: "The market is whatever they can convince you it is."
  },
  {
    myth: "We can't go any lower.",
    reality: "They said that three times before you got this price."
  },
  {
    myth: "This car won't last the weekend.",
    reality: "It's been on the lot for 47 days."
  },
  {
    myth: "Everyone pays doc fees.",
    reality: "Oregon caps them at $150. Everywhere else? It's a free-for-all."
  },
  {
    myth: "You need gap insurance.",
    reality: "Your lender might require it. The dealer just wants the commission."
  },
  {
    myth: "We'll give you top dollar for your trade.",
    reality: "They'll give you wholesale and sell it for retail."
  }
];

export default function BlogSidebar() {
  // Rotate myth based on week of year
  const currentMyth = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now.getTime() - start.getTime();
    const oneWeek = 1000 * 60 * 60 * 24 * 7;
    const weekOfYear = Math.floor(diff / oneWeek);
    return dealerMyths[weekOfYear % dealerMyths.length];
  }, []);

  const quickLinks = [
    {
      title: 'Apply for Financing',
      description: 'Start your financing application today',
      icon: 'ri-calculator-line',
      path: '/apply-for-financing',
      color: 'bg-[#E8B84B]',
    },
    {
      title: 'Value Your Trade',
      description: 'Get an instant trade-in estimate',
      icon: 'ri-exchange-line',
      path: '/trade-in',
      color: 'bg-[#1C2B1E]',
    },
    {
      title: 'Broker Services',
      description: 'We source any car at wholesale pricing',
      icon: 'ri-user-star-line',
      path: '/broker-services',
      color: 'bg-[#E8B84B]',
    },
    {
      title: 'Browse Inventory',
      description: 'View our current vehicle selection',
      icon: 'ri-car-line',
      path: '/inventory',
      color: 'bg-[#1C2B1E]',
    },
  ];

  const cityBrokerGuides = blogPosts.filter(
    post => post.category === 'City Broker Guide'
  );

  const topBuyerGuides = blogPosts
    .filter(p => p.category !== 'City Broker Guide')
    .slice(0, 4);

  const uniqueCategories = Array.from(new Set(blogPosts.map(p => p.category)));
  const categoryCounts = uniqueCategories.map(cat => ({
    label: cat,
    count: blogPosts.filter(p => p.category === cat).length,
  }));

  return (
    <div className="space-y-6">
      {/* Dealer Myth of the Week */}
      <div className="bg-gradient-to-br from-[#1C2B1E] to-[#2A3F2C] rounded-lg p-6 border border-[#E8B84B]/20">
        <div className="flex items-center gap-2 mb-4">
          <i className="ri-error-warning-line text-[#E8B84B] text-xl"></i>
          <h3 className="font-['Oswald'] text-[#E8B84B] text-sm font-semibold tracking-wide uppercase">
            Dealer Myth of the Week
          </h3>
        </div>
        
        <div className="space-y-3">
          <div>
            <div className="text-xs text-red-400 font-semibold mb-1 uppercase tracking-wide">Myth:</div>
            <p className="text-white/90 text-sm font-medium leading-snug">"{currentMyth.myth}"</p>
          </div>
          
          <div>
            <div className="text-xs text-emerald-400 font-semibold mb-1 uppercase tracking-wide">Reality:</div>
            <p className="text-white/90 text-sm font-medium leading-snug">{currentMyth.reality}</p>
          </div>
        </div>

        <Link 
          to="/compare" 
          className="inline-flex items-center gap-1 mt-4 text-[#E8B84B] hover:text-[#F5D576] text-sm font-medium transition-colors group"
        >
          See through the BS
          <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform"></i>
        </Link>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-2xl border border-[#E8E2D6] p-6">
        <h3 className="font-['Oswald'] text-xl font-bold text-[#1C2B1E] mb-4 flex items-center gap-2">
          <i className="ri-links-line text-[#E8B84B]" aria-hidden="true"></i>
          Quick Links
        </h3>
        <div className="space-y-3">
          {quickLinks.map((link, index) => (
            <Link
              key={index}
              to={link.path}
              className="block p-4 bg-[#F7F5F0] rounded-xl border border-[#E8E2D6] hover:border-[#E8B84B] transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 ${link.color} rounded-full flex items-center justify-center flex-shrink-0`} aria-hidden="true">
                  <i className={`${link.icon} text-white text-lg`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-[#1C2B1E] mb-1 group-hover:text-[#E8B84B] transition-colors">
                    {link.title}
                  </h4>
                  <p className="text-xs text-[#374151] line-clamp-2">
                    {link.description}
                  </p>
                </div>
                <i className="ri-arrow-right-line text-[#E8B84B] opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true"></i>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Popular Buyer Guides */}
      {topBuyerGuides.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E8E2D6] p-6">
          <h3 className="font-['Oswald'] text-xl font-bold text-[#1C2B1E] mb-4 flex items-center gap-2">
            <i className="ri-book-open-line text-[#E8B84B]" aria-hidden="true"></i>
            Popular Buyer Guides
          </h3>
          <div className="space-y-3">
            {topBuyerGuides.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="flex gap-3 group hover:bg-[#F7F5F0] rounded-lg p-2 -mx-2 transition-colors"
              >
                <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={post.heroImage}
                    alt={`${post.title} thumbnail`}
                    width={56}
                    height={56}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-[#1C2B1E] group-hover:text-[#E8B84B] transition-colors line-clamp-2 leading-snug">
                    {post.title}
                  </h4>
                  <span className="text-xs text-[#374151] mt-1 block">{post.readTime}</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-[#E8E2D6]">
            <Link
              to="/blog"
              className="text-sm font-semibold text-[#92650a] hover:text-[#1C2B1E] transition-colors inline-flex items-center gap-1"
            >
              View all articles <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      )}

      {/* City Broker Guides */}
      {cityBrokerGuides.length > 0 && (
        <div className="bg-gradient-to-br from-[#1C2B1E] to-[#2d4430] rounded-2xl border border-[#E8E2D6] p-6">
          <h3 className="font-['Oswald'] text-xl font-bold text-white mb-4 flex items-center gap-2">
            <i className="ri-map-pin-line text-[#E8B84B]" aria-hidden="true"></i>
            City Broker Guides
          </h3>
          <div className="space-y-3">
            {cityBrokerGuides.map((post) => {
              const cityMatch = post.title.match(/Auto Broker (.+?)(?:\s+—|\s+:)/);
              const cityName = cityMatch ? cityMatch[1] : post.title;

              return (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="block p-3 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 hover:bg-white/20 hover:border-[#E8B84B] transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#E8B84B] rounded-full flex items-center justify-center flex-shrink-0" aria-hidden="true">
                      <i className="ri-map-pin-2-fill text-[#1C2B1E] text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-white mb-0.5 group-hover:text-[#E8B84B] transition-colors text-sm">
                        {cityName}
                      </h4>
                      <p className="text-xs text-white/80 line-clamp-1">
                        {post.excerpt.substring(0, 60)}...
                      </p>
                    </div>
                    <i className="ri-arrow-right-line text-[#E8B84B] opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true"></i>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-white/20">
            <Link
              to="/broker-services"
              className="text-sm font-semibold text-[#E8B84B] hover:text-white transition-colors inline-flex items-center gap-1"
            >
              View all broker services <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="bg-white rounded-2xl border border-[#E8E2D6] p-6">
        <h3 className="font-['Oswald'] text-xl font-bold text-[#1C2B1E] mb-4 flex items-center gap-2">
          <i className="ri-folder-line text-[#E8B84B]" aria-hidden="true"></i>
          Categories
        </h3>
        <ul className="space-y-2" role="list">
          {categoryCounts.map((category, index) => (
            <li
              key={index}
              className="w-full flex items-center justify-between p-3 bg-[#F7F5F0] rounded-lg"
            >
              <span className="text-sm font-medium text-[#1C2B1E]">{category.label}</span>
              <span className="text-xs text-[#374151] bg-white px-2 py-1 rounded-full border border-[#E8E2D6]">
                {category.count}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Local SEO Links */}
      <div className="bg-white rounded-2xl border border-[#E8E2D6] p-6">
        <h3 className="font-['Oswald'] text-xl font-bold text-[#1C2B1E] mb-4 flex items-center gap-2">
          <i className="ri-map-pin-2-line text-[#E8B84B]" aria-hidden="true"></i>
          Local Pages
        </h3>
        <ul className="space-y-2" role="list">
          {[
            { to: '/service-areas/warrenton', label: 'Used Cars Warrenton OR' },
            { to: '/used-cars-astoria-oregon', label: 'Used Cars Astoria OR' },
            { to: '/used-cars-sw-washington', label: 'Used Cars SW Washington' },
            { to: '/service-areas', label: 'All Service Areas' },
          ].map((link) => (
            <li key={link.to}>
              <Link
                to={link.to}
                className="text-sm text-[#374151] hover:text-[#92650a] transition-colors inline-flex items-center gap-2 group"
              >
                <i className="ri-arrow-right-s-line text-[#E8B84B]" aria-hidden="true"></i>
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Newsletter CTA */}
      <div className="bg-gradient-to-br from-[#1C2B1E] to-[#2d4430] rounded-2xl p-6 text-white">
        <div className="text-center">
          <i className="ri-mail-line text-4xl text-[#E8B84B] mb-3" aria-hidden="true"></i>
          <h3 className="font-['Oswald'] text-xl font-bold mb-2">
            Stay Updated
          </h3>
          <p className="text-sm text-white/90 mb-4">
            Get the latest car buying tips and deals delivered to your inbox
          </p>
          <Link
            to="/blog#newsletter"
            className="inline-block px-6 py-3 bg-[#E8B84B] text-[#1C2B1E] rounded-full font-medium hover:bg-[#d4a43d] transition-colors whitespace-nowrap cursor-pointer"
          >
            Subscribe Now
          </Link>
        </div>
      </div>
    </div>
  );
}
